import React, { Component } from 'react';
import { observer } from 'mobx-react';
import {
    IMobileToolbarProps,
    IMobileToolbarService,
    IMobileToolbarState,
} from '@src/slide/application/service/mobile-tool-bar/core/type';
import ReactView from '@src/slide/infrastructure/module/independent/view-service-define/react/react-view';
import styles from '@com-m/tool-bar-m/index.css';
import FileUpload from '@com-m/property-editor/editor-components/fileupload';
import { MobileEditPanel } from '@com-m/edit-panel-m';
import classnames from 'classnames';
import MobileToolBarModel from '@src/slide/application/service/mobile-tool-bar/core/mobile-toolbar-model';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import Divider from '@tencent/dui-mobile/lib/components/Divider';
import ToolbarItemShape from '@com-m/tool-bar-m/toolbar-item-shape';
import ToolbarItemImage from '@com-m/tool-bar-m/toolbar-item-image';
import { IItemSelectedState } from '@src/slide/application/service/mobile-tool-bar/core/mobile-toolbar-service.interface';

@observer
export default class MobileToolbar extends Component<IMobileToolbarProps, IMobileToolbarState> {
    /**
     * 组件对应的View Service
     * @private
     */
    private mobileToolBarService?: IMobileToolbarService;

    public constructor(props, context) {
        super(props, context);
        this.mobileToolBarService = props.getToolBarService();
        this.mobileToolBarService?.setViewDelegate(new ReactView<IMobileToolbarProps>(this));
    }

    public componentDidMount(): void {
        this.mobileToolBarService?.componentDidMount();
    }

    // eslint-disable-next-line react/no-deprecated
    public componentWillReceiveProps(nextProps) {
        this.mobileToolBarService?.componentWillReceiveProps(nextProps);
    }

    public componentWillUnmount(): void {
        this.mobileToolBarService?.componentWillUnmount();
    }

    public render() {
        if (!this.mobileToolBarService) return null;
        if (!this.mobileToolBarService.shouldRenderToolBar()) {
            return null;
        }
        return this.renderToolbar();
    }

    private renderToolbar = () => {
        const { props } = this;
        const toolbarClass = this.getToolbarCls(props);
        const {
            toolbarData = { items: [] },
            editPanelData = { items: [] },
            insertItemData = { items: [] },
            addonItemData = { items: [] },
        } = this.mobileToolBarService?.getToolBarData() || { };

        const top = this.getState().panelTop < 0 ? -1 : this.getState().panelTop;
        const toolbarExtraStyle = this.mobileToolBarService?.getToolbarExtraStyle();
        const {
            colorPanelType,
            editPanelType,
        } = this.mobileToolBarService?.getPanelType() || { };

        return <React.Fragment>
            <div
                className={toolbarClass}
                style={{
                    verticalAlign: 'middle',
                    bottom: props.isToolbarKeepAtTop ? 'auto' : this.getToolbarBottom(),
                    top: props.isToolbarKeepAtTop ? top : 'auto',
                    ...toolbarExtraStyle,
                }}
                ref={(el) => {
                    this.mobileToolBarService?.setToolbarElement(el);
                }}
            >
                <div className={styles.toolbarContainer}>
                    <div className={styles.toolbarInnerContainer}>
                        {this.renderToolbarItems(toolbarData.items)}
                    </div>
                    {this.renderKeyboardItems(toolbarData.items)}
                    {props.disable ? <div className={styles.disableMask}></div> : null}
                </div>
                <FileUpload
                    useForEdit={true}
                    key={'-1'}
                    maxSize={20000000}
                    onChange={this.handleImageChange}
                    onClear={this.handleImageClear}
                />
                {
                    (colorPanelType && editPanelType) && <MobileEditPanel
                        editItemDatas={editPanelData.items}
                        insertItemDatas={insertItemData.items}
                        addonItemDatas={addonItemData.items}
                        colorPanelType={colorPanelType}
                        getRatingService={this.props.getRatingService}
                        type={editPanelType}
                    />
                }
            </div>
        </React.Fragment>;
    };

    private renderToolbarItems = (toolbarItems) => {
        const itemStyle = {
            height: 44,
        };
        const bottom = this.getToolbarBottom();
        const itemSelectedState: Partial<IItemSelectedState> = this.mobileToolBarService?.getItemSelectedState() || {};
        const { note, textEdit, addEdit, layoutSuggestion, replaceSlideAI } = itemSelectedState;
        return toolbarItems?.map?.((item, i) => {
            const RenderComponent = this.getRenderComponent(item);
            switch (item.id) {
                case ActionType.DIVIDER:
                    return (
                        <Divider
                            key={i}
                            orientation="vertical"
                            style={{
                                verticalAlign: 'middle',
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                margin: '0 4px',
                            }}
                        />
                    );
                case ActionType.NOTE: {
                    /* eslint-disable-next-line no-param-reassign */
                    item.selected = note;
                    return (
                        <RenderComponent
                            key={i}
                            {...item}
                            handleTouchStart={this.handleTouchStart}
                            handleMouseDown={this.handleMouseDown}
                        />
                    );
                }
                case ActionType.LAYOUT_SUGGESTION: {
                    /* eslint-disable-next-line no-param-reassign */
                    item.selected = layoutSuggestion;
                    return (
                        <RenderComponent
                            key={i}
                            {...item}
                            handleTouchStart={this.handleTouchStart}
                            handleMouseDown={this.handleMouseDown}
                        />
                    );
                }
                case ActionType.AI_REPLACE_SLIDE: {
                    /* eslint-disable-next-line no-param-reassign */
                    item.selected = replaceSlideAI;
                    return (
                        <RenderComponent
                            key={i}
                            {...item}
                            handleTouchStart={this.handleTouchStart}
                            handleMouseDown={this.handleMouseDown}
                        />
                    );
                }
                case ActionType.TEXT_EDIT_PANEL: {
                    item.selected = textEdit;
                    return (
                        <RenderComponent
                            key={i}
                            {...item}
                            handleTouchStart={this.handleTouchStart}
                            handleMouseDown={this.handleMouseDown}
                        />
                    );
                }
                case ActionType.ADD_EDIT_PANEL: {
                    item.selected = addEdit;
                    return (
                        <RenderComponent
                            key={i}
                            {...item}
                            handleTouchStart={this.handleTouchStart}
                            handleMouseDown={this.handleMouseDown}
                        />
                    );
                }
                case ActionType.PACKUP_KEYBOARD:
                case ActionType.EVOKE_KEYBOARD: {
                    return null;
                }
                default:
                    return (
                        <RenderComponent
                            key={i}
                            {...item}
                            style={itemStyle}
                            handleTouchStart={this.handleTouchStart}
                            handleMouseDown={this.handleMouseDown}
                            onVisibleChange={this.handleVisibleChange}
                            isToolbarKeepAtTop={this.props.isToolbarKeepAtTop}
                            panelPosition={this.props.isToolbarKeepAtTop ? top : bottom}
                        />
                    );
            }
        }) || null;
    };

    private renderKeyboardItems = (toolbarItems) => {
        const bottom = this.getToolbarBottom();
        const keyboardItem = toolbarItems.filter(item => item.id === ActionType.PACKUP_KEYBOARD
            || item.id === ActionType.EVOKE_KEYBOARD);
        return keyboardItem.length > 0 && <div style={{ width: '48px' }}>
            {keyboardItem?.map?.((item, i) => {
                const RenderComponent = this.getRenderComponent(item);
                return <div className={styles.toolbarRightWrapper} key={item.id}>
                    <div
                        className={styles.toolbarRightDivider}
                    />
                    <RenderComponent
                        key={i}
                        {...item}
                        style={{
                            height: 44,
                            zIndex: 11,
                            backgroundColor: 'white',
                        }}
                        handleTouchStart={this.handleTouchStart}
                        handleMouseDown={this.handleMouseDown}
                        onVisibleChange={this.handleVisibleChange}
                        isToolbarKeepAtTop={this.props.isToolbarKeepAtTop}
                        panelPosition={this.props.isToolbarKeepAtTop ? top : bottom}
                    />
                </div>;
            }) || null}
        </div>;
    };

    private getRenderComponent = (item) => {
        let RenderComponent;
        if (item.icon && item.icon.length > 0) {
            if (item.id === ActionType.INSERT_PICTURE) {
                item.disabled = !this.mobileToolBarService?.isInsertPicBtnEnable();
            }

            switch (item.id) {
                case ActionType.INSERT_SHAPE:
                {
                    RenderComponent = ToolbarItemShape;
                    break;
                }
                default: RenderComponent = ToolbarItemImage;
            }
        }

        return RenderComponent;
    };

    private getToolbarCls(props) {
        const toolbarClass = classnames({
            [styles.toolbar]: props.isToolbarKeepAtTop,
            [styles.toolbar_mobile]: !props.isToolbarKeepAtTop,
            [styles.toolbar_canvas]: true,
        });
        return toolbarClass;
    }

    private getState() {
        const state = this.mobileToolBarService?.getModel();
        return state as MobileToolBarModel;
    }

    private getToolbarBottom(): number {
        return this.mobileToolBarService?.getToolbarBottom() ?? 0;
    }

    private handleImageChange = (imgs) => {
        this.mobileToolBarService?.handleImageChange(imgs);
    };

    private handleImageClear = () => {

    };

    private handleMouseDown = (id, value) => {
        this.mobileToolBarService?.handleMouseDown(id, value);
    };
    private handleTouchStart = (id, value) => {
        this.mobileToolBarService?.handleTouchStart(id, value);
    };

    private handleVisibleChange = () => {};
}
